import { Container, type ContainerOptions } from "../core/Container.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";
import { GlyphButton } from "./GlyphButton.js";
import { Label } from "./Label.js";
import { Panel } from "./Panel.js";
import { Slider } from "./Slider.js";
import { VideoControl, type VideoControlOptions } from "./VideoControl.js";

export interface VideoPlayerOptions extends ContainerOptions, VideoControlOptions {
  /** Height of the transport strip. */
  transportHeight?: number;
  /** Hide the transport and drive playback from code. */
  showTransport?: boolean;
  showVolume?: boolean;
}

const DEFAULT_TRANSPORT_HEIGHT = 40;

/**
 * A `VideoControl` with a transport bar, built from ordinary controls.
 *
 * Two buttons, a slider and a couple of labels — no bespoke painting anywhere,
 * so it inherits every theme for free and looks like the rest of the
 * application rather than like a browser's media chrome. That is also why the
 * surface and the transport are separate classes: this one is an example of
 * assembling the other, and replacing it costs nothing.
 */
export class VideoPlayer extends Container {
  readonly video: VideoControl;
  readonly transport: Panel;
  readonly playButton: GlyphButton;
  readonly seek: Slider;
  readonly muteButton: GlyphButton;
  readonly volumeSlider: Slider;
  readonly timeLabel: Label;

  #showVolume: boolean;
  /** Set while the code is writing the slider, so the write is not read back. */
  #syncing = false;
  /** Set while the user has hold of the seek bar. */
  #scrubbing = false;
  /** Whether playback was running when scrubbing started. */
  #resumeAfterScrub = false;

  constructor(options: VideoPlayerOptions = {}) {
    super({ width: 480, height: 300, ...options });
    this.#showVolume = options.showVolume ?? true;

    this.video = new VideoControl({ ...options, x: 0, y: 0, dock: "fill", name: "video" });
    this.transport = new Panel({
      dock: "bottom",
      height: options.transportHeight ?? DEFAULT_TRANSPORT_HEIGHT,
      style: "flat",
      visible: options.showTransport ?? true,
      name: "transport",
    });

    this.playButton = new GlyphButton({ glyph: "play", width: 30, height: 26, tooltip: "Play" });
    this.playButton.on("click", () => this.video.togglePlay());

    this.seek = new Slider({ minimum: 0, maximum: 1000, value: 0, height: 26 });
    this.seek.tooltip = "Seek";
    this.seek.on("change", () => this.#onSeek());
    // A drag has to stop the clock writing under the thumb, or the two fight
    // each other and the thumb jumps back every time a timeupdate lands.
    this.seek.on("pointerdown", () => this.#beginScrub());
    this.seek.on("pointerup", () => this.#endScrub());

    this.timeLabel = new Label({ text: "0:00 / 0:00", width: 96, height: 26, textAlign: "center" });

    this.muteButton = new GlyphButton({ glyph: "volume", width: 30, height: 26, tooltip: "Mute" });
    this.muteButton.on("click", () => {
      this.video.muted = !this.video.muted;
    });

    this.volumeSlider = new Slider({ minimum: 0, maximum: 100, value: 100, width: 76, height: 26 });
    this.volumeSlider.tooltip = "Volume";
    this.volumeSlider.on("change", () => {
      if (this.#syncing) return;
      this.video.volume = this.volumeSlider.value / 100;
      // Moving the slider off zero is an unmute; nobody expects to have to
      // press the button as well.
      if (this.volumeSlider.value > 0 && this.video.muted) this.video.muted = false;
    });

    this.transport.controls.add(this.playButton);
    this.transport.controls.add(this.seek);
    this.transport.controls.add(this.timeLabel);
    this.transport.controls.add(this.muteButton);
    this.transport.controls.add(this.volumeSlider);

    // The transport first, so `fill` takes what is left rather than the lot.
    this.controls.add(this.transport);
    this.controls.add(this.video);

    for (const event of ["play", "pause", "ended", "timeupdate", "durationchange", "volumechange", "loadedmetadata"] as const) {
      this.video.on(event, () => this.sync());
    }
    this.video.on("mediaerror", (err) => this.emit("mediaerror", err));
    this.sync();
  }

  get showTransport(): boolean {
    return this.transport.visible;
  }
  set showTransport(v: boolean) {
    if (this.transport.visible === v) return;
    this.transport.visible = v;
    this.layout();
  }

  get showVolume(): boolean {
    return this.#showVolume;
  }
  set showVolume(v: boolean) {
    this.#showVolume = v;
    this.performLayout();
    this.invalidate();
  }

  /** Bring the transport into line with the video. Called on every media event. */
  sync(): void {
    const video = this.video;
    const duration = video.duration;
    const playing = !video.paused && !video.ended;

    this.playButton.glyph = playing ? "pause" : "play";
    this.playButton.tooltip = playing ? "Pause" : "Play";
    this.muteButton.glyph = video.muted || video.volume === 0 ? "mute" : "volume";
    this.muteButton.tooltip = video.muted ? "Unmute" : "Mute";

    this.#syncing = true;
    try {
      // A live stream has no length to scrub through, so the bar is disabled
      // rather than left showing a thumb that means nothing.
      const seekable = Number.isFinite(duration) && duration > 0;
      this.seek.enabled = seekable;
      if (seekable && !this.#scrubbing) {
        this.seek.value = Math.round((video.currentTime / duration) * this.seek.maximum);
      } else if (!seekable) {
        this.seek.value = 0;
      }
      const wanted = video.muted ? 0 : Math.round(video.volume * 100);
      if (this.volumeSlider.value !== wanted) this.volumeSlider.value = wanted;
    } finally {
      this.#syncing = false;
    }

    this.timeLabel.text = `${formatTime(video.currentTime)} / ${formatTime(duration)}`;
    this.invalidate();
  }

  /** Release the decoder. The container itself has nothing else to give back. */
  dispose(): void {
    this.video.dispose();
  }

  /**
   * Nothing of its own.
   *
   * The video fills the space and the transport paints itself; a background
   * here would only ever be seen through a letterbox bar the video already
   * draws.
   */
  protected override onPaint(_rc: RenderContext, _theme: Theme): void {}

  protected override performLayout(): void {
    const r = this.transport.clientRect;
    if (r.width <= 0) return;

    const pad = 6;
    const gap = 6;
    const y = Math.round((r.height - this.playButton.height) / 2);
    let left = pad;
    this.playButton.setBounds(left, y, this.playButton.width, this.playButton.height);
    left += this.playButton.width + gap;

    let right = r.width - pad;
    if (this.#showVolume) {
      this.volumeSlider.visible = true;
      this.muteButton.visible = true;
      right -= this.volumeSlider.width;
      this.volumeSlider.setBounds(right, y, this.volumeSlider.width, this.volumeSlider.height);
      right -= gap + this.muteButton.width;
      this.muteButton.setBounds(right, y, this.muteButton.width, this.muteButton.height);
      right -= gap;
    } else {
      this.volumeSlider.visible = false;
      this.muteButton.visible = false;
    }

    right -= this.timeLabel.width;
    this.timeLabel.setBounds(right, y, this.timeLabel.width, this.timeLabel.height);
    right -= gap;

    // Whatever is left in the middle. Never negative: a narrow player collapses
    // the seek bar rather than laying it out backwards.
    this.seek.setBounds(left, y, Math.max(0, right - left), this.seek.height);
  }

  // --- scrubbing -----------------------------------------------------------

  #beginScrub(): void {
    if (!this.seek.enabled || this.#scrubbing) return;
    this.#scrubbing = true;
    this.#resumeAfterScrub = !this.video.paused;
    // Pausing while dragging keeps the seek from racing playback, and stops
    // the audio stuttering through every intermediate position.
    if (this.#resumeAfterScrub) this.video.pause();
  }

  #endScrub(): void {
    if (!this.#scrubbing) return;
    this.#scrubbing = false;
    if (this.#resumeAfterScrub) void this.video.play();
    this.#resumeAfterScrub = false;
    this.sync();
  }

  #onSeek(): void {
    if (this.#syncing) return;
    const duration = this.video.duration;
    if (!Number.isFinite(duration) || duration <= 0) return;
    this.video.currentTime = (this.seek.value / this.seek.maximum) * duration;
    this.timeLabel.text = `${formatTime(this.video.currentTime)} / ${formatTime(duration)}`;
    this.invalidate();
  }
}

/** `m:ss`, or `h:mm:ss` once it needs the hours. */
export function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "--:--";
  const total = Math.floor(seconds);
  const s = total % 60;
  const m = Math.floor(total / 60) % 60;
  const h = Math.floor(total / 3600);
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}
